// ======================================
// Shared Admin Layout Helper
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';

/**
 * Generates the admin layout wrapper with sidebar
 * @param {string} activePage - The active sidebar page key
 * @param {string} pageTitle - Title displayed in the header
 * @param {string} contentHTML - The inner page content HTML
 * @returns {string} Full admin layout HTML
 */
export function renderAdminLayout(activePage, pageTitle, contentHTML) {
  if (!requireAuth(['admin', 'manager', 'designer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const navItems = [
    { href: '/admin', key: 'dashboard', icon: icons.home || '🏠', label: 'Dashboard' },
    { href: '/admin/leads', key: 'leads', icon: icons.users || '👥', label: 'Leads' },
    { href: '/admin/inquiries', key: 'inquiries', icon: icons.mail || '✉️', label: 'Inquiries' },
    { href: '/admin/projects', key: 'projects', icon: icons.briefcase || '💼', label: 'Projects' },
    { href: '/admin/services', key: 'services', icon: icons.tool || '🛠️', label: 'Services' },
    { href: '/admin/testimonials', key: 'testimonials', icon: icons.star || '⭐', label: 'Testimonials' },
    { href: '/admin/about', key: 'about', icon: '🏢', label: 'About Us' },
    { href: '/admin/users', key: 'users', icon: icons.user || '👤', label: 'Users' },
    { href: '/admin/pricing', key: 'pricing', icon: '💰', label: 'Pricing' },
    { href: '/admin/content', key: 'content', icon: '📝', label: 'Content' },
    { href: '/admin/analytics', key: 'analytics', icon: '📊', label: 'Analytics' },
    { href: '/admin/crm', key: 'crm', icon: '🤝', label: 'CRM' },
  ];

  const navHTML = navItems.map(item => `
    <a href="${item.href}" class="admin-nav-item${item.key === activePage ? ' active' : ''}" data-page="${item.key}">
      ${item.icon}
      <span>${item.label}</span>
    </a>
  `).join('');

  return `
    <div class="admin-layout">
      <aside class="admin-sidebar">
        <div class="admin-sidebar-header">
          <div class="admin-logo">
            <span class="logo-text">NVI</span>
            <span class="logo-badge">Admin</span>
          </div>
        </div>
        
        <nav class="admin-nav">
          ${navHTML}
        </nav>
        
        <div class="admin-sidebar-footer">
          <a href="/" class="admin-nav-item">
            ${icons.globe || '🌐'}
            <span>View Site</span>
          </a>
          <button class="admin-nav-item admin-logout" id="admin-logout">
            ${icons.logOut || '🚪'}
            <span>Logout</span>
          </button>
        </div>
      </aside>
      
      <main class="admin-main">
        <header class="admin-header">
          <div class="admin-header-left">
            <button class="admin-menu-toggle" id="admin-menu-toggle">☰</button>
            <h1 class="admin-page-title">${pageTitle}</h1>
          </div>
          <div class="admin-header-right">
            <div class="admin-user">
              <span class="admin-user-name">${user?.name || 'Admin'}</span>
              <span class="admin-user-role">${user?.role || 'admin'}</span>
            </div>
          </div>
        </header>
        
        <div class="admin-content">
          ${contentHTML}
        </div>
      </main>
    </div>
  `;
}

/**
 * Initialize common admin layout event listeners (logout, mobile menu)
 */
export function initAdminLayout() {
  const logoutBtn = document.getElementById('admin-logout');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => logout());
  }

  const menuToggle = document.getElementById('admin-menu-toggle');
  const sidebar = document.querySelector('.admin-sidebar');
  if (menuToggle && sidebar) {
    menuToggle.addEventListener('click', () => sidebar.classList.toggle('open'));
  }
}
